import React, { useState, useMemo } from "react";
import styles from "./CampaignAnalysis.module.scss";
import { Card, Row, Col, Table, Select, Input, Typography, Tag, Space, DatePicker } from "antd";
import {
  ArrowUpOutlined, ArrowDownOutlined, SearchOutlined, BulbOutlined,
  TeamOutlined, BarChartOutlined, ShopOutlined, TrophyOutlined, StarOutlined,
} from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const fmt = (v: number) => `$${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => v.toLocaleString("es-AR");

const Vr: React.FC<{ value: number; suffix?: string }> = ({ value, suffix = "%" }) => {
  const pos = value >= 0;
  return (
    <span className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}>
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {pos ? "+" : ""}{value.toFixed(1)}{suffix}
    </span>
  );
};

/* ─── hardcoded data ─── */

interface Campaign {
  id: number;
  nombre: string;
  tipo: string;
  canal: string;
  ventaAtribuida: number;
  ventaEsperada: number;
  costoCampaña: number;
  costoProducto: number;
  clientesNuevos: number;
  clientesReactivados: number;
  clientesFrecuentes: number;
  clientesImpactados: number;
  pedidos: number;
  comprasPosteriores: number;
}

const campaigns: Campaign[] = [
  { id: 1, nombre: "Black Friday Web", tipo: "Promocional", canal: "eCommerce", ventaAtribuida: 8200000, ventaEsperada: 5400000, costoCampaña: 620000, costoProducto: 4510000, clientesNuevos: 420, clientesReactivados: 180, clientesFrecuentes: 1200, clientesImpactados: 4800, pedidos: 2800, comprasPosteriores: 1.8 },
  { id: 2, nombre: "Reactivación Clientes Dormidos", tipo: "Reactivación", canal: "CRM / Email", ventaAtribuida: 2400000, ventaEsperada: 800000, costoCampaña: 95000, costoProducto: 1200000, clientesNuevos: 0, clientesReactivados: 640, clientesFrecuentes: 120, clientesImpactados: 3200, pedidos: 920, comprasPosteriores: 2.4 },
  { id: 3, nombre: "Lanzamiento Línea Urbana", tipo: "Lanzamiento", canal: "Omnicanal", ventaAtribuida: 4600000, ventaEsperada: 2800000, costoCampaña: 420000, costoProducto: 2530000, clientesNuevos: 680, clientesReactivados: 95, clientesFrecuentes: 850, clientesImpactados: 5400, pedidos: 1950, comprasPosteriores: 1.5 },
  { id: 4, nombre: "Newsletter Semanal Q1", tipo: "Newsletter", canal: "CRM / Email", ventaAtribuida: 1800000, ventaEsperada: 1400000, costoCampaña: 48000, costoProducto: 900000, clientesNuevos: 85, clientesReactivados: 45, clientesFrecuentes: 2400, clientesImpactados: 8500, pedidos: 1200, comprasPosteriores: 2.1 },
  { id: 5, nombre: "Campaña Día de la Madre", tipo: "Promocional", canal: "Tiendas físicas", ventaAtribuida: 5200000, ventaEsperada: 3600000, costoCampaña: 380000, costoProducto: 2860000, clientesNuevos: 310, clientesReactivados: 220, clientesFrecuentes: 1600, clientesImpactados: 4200, pedidos: 2400, comprasPosteriores: 1.6 },
  { id: 6, nombre: "Instagram Running Challenge", tipo: "Editorial / contenido", canal: "Social", ventaAtribuida: 1200000, ventaEsperada: 600000, costoCampaña: 180000, costoProducto: 540000, clientesNuevos: 520, clientesReactivados: 30, clientesFrecuentes: 280, clientesImpactados: 12000, pedidos: 680, comprasPosteriores: 1.2 },
  { id: 7, nombre: "Outlet Fin de Temporada", tipo: "Promocional", canal: "Tiendas físicas", ventaAtribuida: 3800000, ventaEsperada: 2600000, costoCampaña: 290000, costoProducto: 2470000, clientesNuevos: 140, clientesReactivados: 310, clientesFrecuentes: 900, clientesImpactados: 2800, pedidos: 1800, comprasPosteriores: 1.3 },
  { id: 8, nombre: "Reactivación VIP Q2", tipo: "Reactivación", canal: "CRM / Email", ventaAtribuida: 1600000, ventaEsperada: 500000, costoCampaña: 72000, costoProducto: 720000, clientesNuevos: 0, clientesReactivados: 380, clientesFrecuentes: 60, clientesImpactados: 1800, pedidos: 520, comprasPosteriores: 2.8 },
  { id: 9, nombre: "Marketplace Push Verano", tipo: "Promocional", canal: "Marketplace", ventaAtribuida: 2800000, ventaEsperada: 2000000, costoCampaña: 340000, costoProducto: 1680000, clientesNuevos: 240, clientesReactivados: 60, clientesFrecuentes: 480, clientesImpactados: 3600, pedidos: 1100, comprasPosteriores: 1.4 },
  { id: 10, nombre: "Blog + SEO Calzado Formal", tipo: "Editorial / contenido", canal: "eCommerce", ventaAtribuida: 900000, ventaEsperada: 650000, costoCampaña: 60000, costoProducto: 405000, clientesNuevos: 180, clientesReactivados: 15, clientesFrecuentes: 320, clientesImpactados: 6200, pedidos: 420, comprasPosteriores: 1.1 },
];

const getEstado = (roi: number, margen: number, incr: number, nuevos: number, react: number): { label: string; color: string } => {
  if (roi > 3 && margen > 40) return { label: "Escalar", color: "green" };
  if (roi > 2 && margen > 35) return { label: "Muy eficiente", color: "blue" };
  if (roi > 1.5 && (nuevos > 200 || react > 200)) return { label: "Repetir", color: "cyan" };
  if (roi > 1 && margen > 30) return { label: "Selectiva", color: "gold" };
  return { label: "Revisar descuento", color: "orange" };
};

const getPrioridad = (roi: number, margen: number, incr: number, nuevos: number, react: number): { accion: string; motivo: string; prioridad: number } | null => {
  const avgRoi = campaigns.reduce((s, c) => s + (c.ventaAtribuida - c.ventaEsperada) / c.costoCampaña, 0) / campaigns.length;
  if (roi <= avgRoi || margen <= 0 || incr <= 0) return null;
  if (roi > 3 && margen > 45) return { accion: "Escalar inmediatamente", motivo: "Alta eficiencia en ROI y margen excelente.", prioridad: 1 };
  if (roi > 2 && (react > 200 || nuevos > 200)) return { accion: "Repetir con prioridad", motivo: "Fuerte recuperación/adquisición de clientes con retorno sólido.", prioridad: 1 };
  if (roi > 1.5 && margen > 35) return { accion: "Repetir", motivo: "Buen equilibrio entre rentabilidad e impacto incremental.", prioridad: 2 };
  if (nuevos > 300) return { accion: "Repetir (adquisición)", motivo: "Fuerte generación de clientes nuevos.", prioridad: 2 };
  return { accion: "Evaluar repetición", motivo: "ROI aceptable, analizar ajustes para mejorar margen.", prioridad: 3 };
};

/* type analysis */
const groupByType = () => {
  const types = Array.from(new Set(campaigns.map((c) => c.tipo)));
  return types.map((t, i) => {
    const group = campaigns.filter((c) => c.tipo === t);
    const totalVenta = group.reduce((s, c) => s + c.ventaAtribuida, 0);
    const totalIncr = group.reduce((s, c) => s + (c.ventaAtribuida - c.ventaEsperada), 0);
    const totalCosto = group.reduce((s, c) => s + c.costoCampaña, 0);
    const totalCostoProd = group.reduce((s, c) => s + c.costoProducto, 0);
    const totalNuevos = group.reduce((s, c) => s + c.clientesNuevos, 0);
    return {
      key: i + 1,
      tipo: t,
      cantidad: group.length,
      ventaAtribuida: totalVenta,
      ventaIncremental: totalIncr,
      clientesNuevos: totalNuevos,
      roi: totalCosto > 0 ? totalIncr / totalCosto : 0,
      margen: totalVenta > 0 ? ((totalVenta - totalCostoProd) / totalVenta) * 100 : 0,
    };
  });
};

/* store analysis */
const storeData = [
  { key: 1, tienda: "Tienda Central", campañas: 6, ventaAtribuida: 5800000, ventaIncremental: 2200000, roi: 2.8, margen: 44.2 },
  { key: 2, tienda: "Sucursal Norte", campañas: 5, ventaAtribuida: 3200000, ventaIncremental: 1100000, roi: 2.1, margen: 41.5 },
  { key: 3, tienda: "Sucursal Sur", campañas: 4, ventaAtribuida: 2400000, ventaIncremental: 800000, roi: 1.6, margen: 39.8 },
  { key: 4, tienda: "Tienda Online", campañas: 8, ventaAtribuida: 9100000, ventaIncremental: 4200000, roi: 3.1, margen: 46.8 },
  { key: 5, tienda: "Outlet Norte", campañas: 3, ventaAtribuida: 1800000, ventaIncremental: 600000, roi: 1.2, margen: 34.2 },
  { key: 6, tienda: "Marketplace Hub", campañas: 2, ventaAtribuida: 2800000, ventaIncremental: 800000, roi: 2.4, margen: 40.0 },
];

const insights = [
  "Pendiente: implementar motor de análisis automático de campañas.",
];

const CampaignAnalysis: React.FC = () => {
  const [search, setSearch] = useState("");

  /* KPIs */
  const totalVentaAtribuida = campaigns.reduce((s, c) => s + c.ventaAtribuida, 0);
  const totalIncremental = campaigns.reduce((s, c) => s + (c.ventaAtribuida - c.ventaEsperada), 0);
  const totalCostoCamp = campaigns.reduce((s, c) => s + c.costoCampaña, 0);
  const totalCostoProd = campaigns.reduce((s, c) => s + c.costoProducto, 0);
  const totalNuevos = campaigns.reduce((s, c) => s + c.clientesNuevos, 0);
  const totalReactivados = campaigns.reduce((s, c) => s + c.clientesReactivados, 0);
  const avgROI = totalCostoCamp > 0 ? totalIncremental / totalCostoCamp : 0;
  const avgMargen = totalVentaAtribuida > 0 ? ((totalVentaAtribuida - totalCostoProd) / totalVentaAtribuida) * 100 : 0;

  const filtered = useMemo(() => {
    if (!search.trim()) return campaigns;
    const q = search.toLowerCase();
    return campaigns.filter((c) => c.nombre.toLowerCase().includes(q) || c.tipo.toLowerCase().includes(q));
  }, [search]);

  const typeData = useMemo(() => groupByType(), []);

  /* profitable campaigns */
  const profitable = useMemo(() => {
    return campaigns
      .map((c) => {
        const incr = c.ventaAtribuida - c.ventaEsperada;
        const roi = c.costoCampaña > 0 ? incr / c.costoCampaña : 0;
        const margen = c.ventaAtribuida > 0 ? ((c.ventaAtribuida - c.costoProducto) / c.ventaAtribuida) * 100 : 0;
        const rec = getPrioridad(roi, margen, incr, c.clientesNuevos, c.clientesReactivados);
        if (!rec) return null;
        return { key: c.id, nombre: c.nombre, roi, margen, ...rec };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => a.prioridad - b.prioridad || b.roi - a.roi) as Array<{ key: number; nombre: string; roi: number; margen: number; accion: string; motivo: string; prioridad: number }>;
  }, []);

  const rankingCols = [
    { title: "Campaña", key: "nombre", render: (_: any, r: Campaign) => <Text strong>{r.nombre}</Text> },
    { title: "Tipo", dataIndex: "tipo", key: "tipo", render: (v: string) => <Tag>{v}</Tag> },
    { title: "Canal", dataIndex: "canal", key: "canal", width: 130 },
    { title: "Venta atribuida", key: "ventaAtribuida", render: (_: any, r: Campaign) => fmt(r.ventaAtribuida), align: "right" as const, sorter: (a: Campaign, b: Campaign) => a.ventaAtribuida - b.ventaAtribuida },
    { title: "Venta incremental", key: "ventaIncr", render: (_: any, r: Campaign) => fmt(r.ventaAtribuida - r.ventaEsperada), align: "right" as const, sorter: (a: Campaign, b: Campaign) => (a.ventaAtribuida - a.ventaEsperada) - (b.ventaAtribuida - b.ventaEsperada) },
    { title: "Nuevos", dataIndex: "clientesNuevos", key: "clientesNuevos", render: (v: number) => fmtN(v), align: "right" as const, sorter: (a: Campaign, b: Campaign) => a.clientesNuevos - b.clientesNuevos },
    { title: "Reactivados", dataIndex: "clientesReactivados", key: "clientesReactivados", render: (v: number) => fmtN(v), align: "right" as const, sorter: (a: Campaign, b: Campaign) => a.clientesReactivados - b.clientesReactivados },
    { title: "ROI", key: "roi", render: (_: any, r: Campaign) => { const roi = r.costoCampaña > 0 ? (r.ventaAtribuida - r.ventaEsperada) / r.costoCampaña : 0; return <Text strong>{roi.toFixed(1)}x</Text>; }, align: "right" as const, sorter: (a: Campaign, b: Campaign) => ((a.ventaAtribuida - a.ventaEsperada) / a.costoCampaña) - ((b.ventaAtribuida - b.ventaEsperada) / b.costoCampaña) },
    { title: "Margen", key: "margen", render: (_: any, r: Campaign) => `${(((r.ventaAtribuida - r.costoProducto) / r.ventaAtribuida) * 100).toFixed(1)}%`, align: "right" as const },
    {
      title: "Estado", key: "estado",
      render: (_: any, r: Campaign) => {
        const incr = r.ventaAtribuida - r.ventaEsperada;
        const roi = r.costoCampaña > 0 ? incr / r.costoCampaña : 0;
        const margen = r.ventaAtribuida > 0 ? ((r.ventaAtribuida - r.costoProducto) / r.ventaAtribuida) * 100 : 0;
        const est = getEstado(roi, margen, incr, r.clientesNuevos, r.clientesReactivados);
        return <Tag color={est.color}>{est.label}</Tag>;
      },
    },
  ];

  const clientImpactCols = [
    { title: "Campaña", key: "nombre", render: (_: any, r: Campaign) => <Text strong>{r.nombre}</Text> },
    { title: "Nuevos", dataIndex: "clientesNuevos", key: "clientesNuevos", render: (v: number) => fmtN(v), align: "right" as const },
    { title: "Reactivados", dataIndex: "clientesReactivados", key: "clientesReactivados", render: (v: number) => fmtN(v), align: "right" as const },
    { title: "Frecuentes impactados", dataIndex: "clientesFrecuentes", key: "clientesFrecuentes", render: (v: number) => fmtN(v), align: "right" as const },
    { title: "Tasa conversión", key: "tasaConv", render: (_: any, r: Campaign) => `${((r.pedidos / r.clientesImpactados) * 100).toFixed(1)}%`, align: "right" as const },
    { title: "Ticket medio", key: "ticketMedio", render: (_: any, r: Campaign) => fmt(Math.round(r.ventaAtribuida / r.pedidos)), align: "right" as const },
    { title: "Frec. posterior", dataIndex: "comprasPosteriores", key: "comprasPosteriores", render: (v: number) => `${v.toFixed(1)}x`, align: "right" as const },
  ];

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>Campañas</Title>

      {/* FILTERS */}
      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <Input placeholder="Buscar campaña..." prefix={<SearchOutlined />} value={search} onChange={(e) => setSearch(e.target.value)} className={styles.searchInput} allowClear />
          <div className={styles.filterItem}><Text className={styles.filterLabel}>Período</Text><RangePicker className={styles.filterInput} /></div>
          <div className={styles.filterItem}><Text className={styles.filterLabel}>Tipo</Text><Select placeholder="Todos" allowClear className={styles.filterSelect} options={[{ value: "promo", label: "Promocional" }, { value: "news", label: "Newsletter" }, { value: "react", label: "Reactivación" }, { value: "edit", label: "Editorial / contenido" }, { value: "launch", label: "Lanzamiento" }]} /></div>
          <div className={styles.filterItem}><Text className={styles.filterLabel}>Canal</Text><Select placeholder="Todos" allowClear className={styles.filterSelect} options={[{ value: "fisico", label: "Tiendas físicas" }, { value: "ecomm", label: "eCommerce" }, { value: "mkt", label: "Marketplace" }, { value: "omni", label: "Omnicanal" }, { value: "crm", label: "CRM / Email" }, { value: "social", label: "Social" }]} /></div>
          <div className={styles.filterItem}><Text className={styles.filterLabel}>Segmento</Text><Select placeholder="Todos" allowClear className={styles.filterSelect} options={[{ value: "all", label: "Todos" }, { value: "freq", label: "Frecuentes" }, { value: "new", label: "Nuevos" }, { value: "dormidos", label: "Dormidos" }]} /></div>
        </div>
      </Card>

      {/* KPIs */}
      <div className={styles.kpiRow}>
        {[
          { label: "Campañas ejecutadas", value: String(campaigns.length) },
          { label: "Venta atribuida", value: fmt(totalVentaAtribuida) },
          { label: "Clientes nuevos", value: fmtN(totalNuevos) },
          { label: "Clientes reactivados", value: fmtN(totalReactivados) },
          { label: "Venta incremental", value: fmt(totalIncremental) },
          { label: "ROI promedio", value: `${avgROI.toFixed(1)}x` },
          { label: "Margen medio", value: `${avgMargen.toFixed(1)}%` },
        ].map((k, i) => (
          <Card className={styles.kpiCard} key={i}>
            <Text className={styles.kpiLabel}>{k.label}</Text>
            <div className={styles.kpiValue}>{k.value}</div>
          </Card>
        ))}
      </div>

      {/* RANKING TABLE */}
      <Card className={styles.tableCard}>
        <Table columns={rankingCols} dataSource={filtered.map((c) => ({ ...c, key: c.id }))} pagination={{ pageSize: 15, showSizeChanger: false }} size="small" scroll={{ x: 1100 }} className={styles.dataTable} />
      </Card>

      {/* INSIGHTS */}
      <Card className={styles.sectionCard} title={<Space><BulbOutlined style={{ color: "#faad14" }} /><span>Insights</span></Space>}>
        <div className={styles.bulletList}>
          {insights.map((ins, i) => (
            <div key={i} className={styles.bulletItem}>
              <BulbOutlined className={styles.bYellow} />
              <Text type="secondary">{ins}</Text>
            </div>
          ))}
        </div>
      </Card>

      {/* CLIENT IMPACT */}
      <Card className={styles.sectionCard} title={<Space><TeamOutlined /><span>Impacto en clientes</span></Space>}>
        <Table columns={clientImpactCols} dataSource={campaigns.map((c) => ({ ...c, key: c.id }))} pagination={false} size="small" scroll={{ x: 900 }} className={styles.dataTable} />
      </Card>

      {/* TYPE ANALYSIS + STORE ANALYSIS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card className={styles.sectionCard} title={<Space><BarChartOutlined /><span>Análisis por tipo de campaña</span></Space>}>
            <Table columns={[
              { title: "Tipo", dataIndex: "tipo", key: "tipo" },
              { title: "Cantidad", dataIndex: "cantidad", key: "cantidad", align: "center" as const },
              { title: "Venta atribuida", dataIndex: "ventaAtribuida", key: "ventaAtribuida", render: (v: number) => fmt(v), align: "right" as const },
              { title: "Venta incr.", dataIndex: "ventaIncremental", key: "ventaIncremental", render: (v: number) => fmt(v), align: "right" as const },
              { title: "Nuevos", dataIndex: "clientesNuevos", key: "clientesNuevos", render: (v: number) => fmtN(v), align: "right" as const },
              { title: "ROI prom.", dataIndex: "roi", key: "roi", render: (v: number) => `${v.toFixed(1)}x`, align: "right" as const },
              { title: "Margen prom.", dataIndex: "margen", key: "margen", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
            ]} dataSource={typeData} pagination={false} size="small" className={styles.dataTable} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className={styles.sectionCard} title={<Space><ShopOutlined /><span>Análisis por tienda</span></Space>}>
            <Table columns={[
              { title: "Tienda", dataIndex: "tienda", key: "tienda" },
              { title: "Campañas", dataIndex: "campañas", key: "campañas", align: "center" as const },
              { title: "Venta atribuida", dataIndex: "ventaAtribuida", key: "ventaAtribuida", render: (v: number) => fmt(v), align: "right" as const },
              { title: "Venta incr.", dataIndex: "ventaIncremental", key: "ventaIncremental", render: (v: number) => fmt(v), align: "right" as const },
              { title: "ROI", dataIndex: "roi", key: "roi", render: (v: number) => `${v.toFixed(1)}x`, align: "right" as const },
              { title: "Margen", dataIndex: "margen", key: "margen", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
            ]} dataSource={storeData} pagination={false} size="small" className={styles.dataTable} />
          </Card>
        </Col>
      </Row>

      {/* PROFITABLE CAMPAIGNS */}
      <Card className={styles.sectionCard} title={<Space><TrophyOutlined style={{ color: "#52c41a" }} /><span>Campañas rentables para repetir</span></Space>}>
        <div className={styles.profitableList}>
          {profitable.map((p) => (
            <div key={p.key} className={styles.profitableItem}>
              <div className={styles.profitableHeader}>
                <Tag color={p.prioridad === 1 ? "green" : p.prioridad === 2 ? "blue" : "gold"}>Prioridad {p.prioridad}</Tag>
                <Text strong>{p.nombre}</Text>
                <Tag style={{ marginLeft: 8 }}>{p.accion}</Tag>
              </div>
              <Text type="secondary" className={styles.profitableMotivo}>{p.motivo}</Text>
              <Space size="middle" className={styles.profitableMetrics}>
                <Text type="secondary">ROI: <Text strong>{p.roi.toFixed(1)}x</Text></Text>
                <Text type="secondary">Margen: <Text strong>{p.margen.toFixed(1)}%</Text></Text>
              </Space>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default CampaignAnalysis;
